import { useState, useEffect, useMemo, useCallback, FC } from 'react';
import { Box, Button, TextField, Tooltip, Typography, IconButton, Autocomplete, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, ListItemText, Checkbox } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { showSuccessSnackbar, showErrorSnackbar } from '../../utils/Snackbar';
import { CommHandler } from '../../utils';
import { config } from '../../config';
import { getAuthHeaderDynamic } from '../../utils/utils';

const info = {
  groupName: 'Please enter the group name',
  level: 'Please enter the level (numbers only)',
  department: 'Please select the department',
  designations: 'Please select the designation/s',
};

type Section = {
  id: string;
  groupName: string;
  level: string;
  // Change single department to multi-select departments (store names)
  department: string[]; // array of selected department names
  department_ids?: number[]; // array of selected department ids (derived)
  designations: string[];
};

type BuItem = {
  id: number;
  department: string;
};

interface AddEditDesignationFormProps {
  onClose?: () => void;
  initialData?: Section[] | null;
}

const emptySection: Section = { id: 'group-1', groupName: '', level: '', department: [], department_ids: [], designations: [] };

const AddEditDesignationForm: FC<AddEditDesignationFormProps> = ({ onClose, initialData }) => {
  // Add global style for the specific class
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .css-11bgqu7 {
        width: 98% !important;
        height: 100vh !important;
        margin: auto !important;
        border-radius: 12px !important;
      }
    `;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const navigate = useNavigate();
  const location = useLocation();

  // If navigated with state, use that as initial data
  const getInitialFormGroups = useCallback(() => {
    // Priority: location.state.data > initialData > empty
    const navData = location.state?.data;
    if (navData) {
      // Convert navData (single object) to Section[]
      // Try to map designations to names if they are IDs
      let designationsArr = [];
      if (Array.isArray(navData.designations)) {
        designationsArr = navData.designations;
      } else if (typeof navData.designations === 'string' && navData.designations !== '') {
        designationsArr = navData.designations.split(',').map((d: string) => d.trim());
      }
      // We'll map these IDs/names to the actual names after availableDesignations is loaded
      // Keep level as is (numeric value)
      const levelValue = navData.level ? String(navData.level) : '';

      return [
        {
          id: navData.id ? String(navData.id) : 'group-1',
          groupName: navData.groupName || '',
          level: levelValue,
          // Split department string (comma / slash separated) into array if present
          department: navData.department
            ? String(navData.department)
                .split(/[,/]/)
                .map((d: string) => d.trim())
                .filter(Boolean)
            : [],
          department_ids: navData.department_id ? [navData.department_id] : [],
          designations: designationsArr,
        },
      ];
    }
    if (initialData) {
      return initialData;
    }
    return [{ ...emptySection }];
  }, [location.state, initialData]);

  const [formGroups, setFormGroups] = useState<Section[]>(() => getInitialFormGroups());
  const [errors, setErrors] = useState<Record<string, Record<string, string>>>({});
  const [availableDesignations, setAvailableDesignations] = useState<{ id: string; name: string }[]>([]);
  const [buData, setBuData] = useState<BuItem[]>([]);
  const [loading, setLoading] = useState({
    designations: false,
    buData: false,
    allocatedDesignations: false,
  });
  const [backendError, setBackendError] = useState<string>('');
  const [existingDesignationGroups, setExistingDesignationGroups] = useState<any[]>([]);
  const [allocatedDesignationIds, setAllocatedDesignationIds] = useState<string[]>([]);

  // Deduplicated designation options (allocated ones are shown after unallocated)
  const designationOptions = useMemo(() => {
    const seenNames = new Set<string>();
    const deduped = availableDesignations.filter((d) => {
      const nameKey = String((d.name || '').trim()).toLowerCase();
      if (!nameKey) return false;
      if (seenNames.has(nameKey)) return false; // drop duplicate name
      seenNames.add(nameKey);
      return true;
    });

    return deduped.sort((a, b) => {
      const aAllocated = allocatedDesignationIds.includes(String(a.id));
      const bAllocated = allocatedDesignationIds.includes(String(b.id));
      if (aAllocated === bAllocated) {
        const aName = String(a.name || '').toLowerCase();
        const bName = String(b.name || '').toLowerCase();
        if (aName === bName) return 0;
        return aName < bName ? -1 : 1;
      }
      return aAllocated ? 1 : -1;
    });
  }, [availableDesignations, allocatedDesignationIds]);

  // If user navigates with new state, update formGroups
  useEffect(() => {
    const navData = location.state?.data;
    if (navData) {
      setFormGroups(getInitialFormGroups());
    } else if (initialData) {
      setFormGroups(initialData);
    }
  }, [location.state, initialData, getInitialFormGroups]);

  // After availableDesignations is loaded, map designations to names if needed, and always update formGroups so Autocomplete value works
  useEffect(() => {
    setFormGroups((prevGroups) =>
      prevGroups.map((group) => {
        if (!group.designations || group.designations.length === 0) return group;
        // Map each designation (id or name) to the corresponding name if possible
        const mapped = group.designations.map((d) => {
          const dStr = String(d);
          const found = availableDesignations.find((ad) => String(ad.id) === dStr || ad.name === dStr);
          return found ? String(found.name) : dStr; // ensure string
        });
        // Deduplicate & ensure all are strings
        const unique = Array.from(new Set(mapped.map((m) => String(m).trim()))).filter(Boolean);
        return { ...group, designations: unique };
      }),
    );
  }, [availableDesignations]);

  // Function to fetch existing designation groups
  const fetchExistingDesignationGroups = useCallback(async () => {
    try {
      setLoading((prev) => ({ ...prev, allocatedDesignations: true }));
      const response = await CommHandler.request({
        url: `${config.apiBaseUrl}/evaluation/designation-group`,
        method: 'GET',
        headers: getAuthHeaderDynamic(),
      });

      if (response.status === 200) {
        const groups = response.data || [];
        setExistingDesignationGroups(groups);

        // Extract all allocated designation IDs
        const allocatedIds: string[] = [];
        groups.forEach((group: any) => {
          if (group.departments) {
            group.departments.forEach((dept: any) => {
              if (dept.designations) {
                dept.designations.forEach((designation: any) => {
                  allocatedIds.push(String(designation.id));
                });
              }
            });
          }
        });

        // If in edit mode, exclude current group's designations from allocated list
        const currentGroupId = location.state?.data?.id;
        if (currentGroupId) {
          const currentGroup = groups.find((g: any) => g.id === Number(currentGroupId));
          if (currentGroup && currentGroup.departments) {
            currentGroup.departments.forEach((dept: any) => {
              if (dept.designations) {
                dept.designations.forEach((designation: any) => {
                  const index = allocatedIds.indexOf(String(designation.id));
                  if (index > -1) {
                    allocatedIds.splice(index, 1);
                  }
                });
              }
            });
          }
        }

        setAllocatedDesignationIds(allocatedIds);
      } else {
        showErrorSnackbar('Failed to fetch existing designation groups');
      }
    } catch (err) {
      console.error('Error fetching existing designation groups:', err);
      showErrorSnackbar('Error fetching existing designation groups');
    } finally {
      setLoading((prev) => ({ ...prev, allocatedDesignations: false }));
    }
  }, [location.state?.data?.id]);

  useEffect(() => {
    if (existingDesignationGroups.length > 0) {
      fetchExistingDesignationGroups();
    }
  }, [location.state?.data?.id, existingDesignationGroups.length, fetchExistingDesignationGroups]);

  // Function to fetch Business Unit data from the API
  const fetchBuData = useCallback(async () => {
    try {
      const response = await CommHandler.request({
        url: `${config.apiBaseUrl}/bu-profiles`,
        method: 'GET',
        headers: getAuthHeaderDynamic(),
      });

      if (response.status === 200) {
        const formattedBuData = response.data.map((bu: any) => ({
          id: bu.id,
          department: bu.department,
        }));
        setBuData(formattedBuData);
      } else {
        showErrorSnackbar('Failed to fetch business units');
      }
    } catch (err) {
      showErrorSnackbar('Error fetching business units');
    }
  }, []);

  // Function to fetch designations from the API
  const fetchDesignations = useCallback(async () => {
    try {
      const response = await CommHandler.request({
        url: `${config.apiBaseUrl}/designation/all/designations`,
        method: 'GET',
        headers: getAuthHeaderDynamic(),
      });

      if (response.status === 200) {
        const formattedDesignations = response.data.map((designation: any) => ({
          id: designation.id,
          name: designation.name || designation.designation || designation.title,
        }));
        setAvailableDesignations(formattedDesignations);
      } else {
        showErrorSnackbar('Failed to fetch designations');
      }
    } catch (err) {
      showErrorSnackbar('Error fetching designations');
    }
  }, []);

  // Fetch designations and BU data from API when component mounts
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        // Fetch all data in parallel
        await Promise.all([fetchDesignations(), fetchBuData(), fetchExistingDesignationGroups()]);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchAllData();
  }, [fetchDesignations, fetchBuData, fetchExistingDesignationGroups]);

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const handleChange = (groupId: string, field: keyof Section, value: any) => {
    // Clear backend error when user makes changes
    if (backendError) {
      setBackendError('');
    }

    // Process value to prevent leading spaces for groupName field
    let processedValue = value;
    if (field === 'groupName' && typeof value === 'string' && value.startsWith(' ')) {
      processedValue = value.trimStart();
    }

    if (field === 'department') {
      // Multi-select departments => value is string[]
      const selectedNames: string[] = Array.isArray(processedValue) ? processedValue : [];
      const ids = buData.filter((bu) => selectedNames.includes(bu.department)).map((bu) => bu.id);
      setFormGroups((prev) => prev.map((group) => (group.id === groupId ? { ...group, department: selectedNames, department_ids: ids } : group)));
    } else {
      // For other fields, just update normally
      setFormGroups((prev) => prev.map((group) => (group.id === groupId ? { ...group, [field]: processedValue } : group)));
    }

    // Clear error when user makes a change
    if (errors[groupId]?.[field]) {
      setErrors((prev) => {
        const updatedGroupErrors = { ...prev[groupId] };
        delete updatedGroupErrors[field];
        return {
          ...prev,
          [groupId]: updatedGroupErrors,
        };
      });
    }
  };

  const handleBack = () => {
    // Check if there's unsaved data
    // If there's any form data entered, show confirmation dialog
    const hasData = formGroups.some((group) => group.level !== '' || (Array.isArray(group.department) && group.department.length > 0) || group.designations.length > 0);

    if (hasData) {
      setOpenConfirmDialog(true);
    } else {
      onClose?.();
      navigate('/evaluation', { state: { tabIndex: 0 } });
    }
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleDiscard = () => {
    setOpenConfirmDialog(false);
    onClose?.();
    navigate('/evaluation', { state: { tabIndex: 0 } });
  };

  const addDesignationGroup = () => {
    const newId = `group-${formGroups.length + 1}`;
    setFormGroups((prev) => [...prev, { ...emptySection, id: newId }]);
  };

  const removeDesignationGroup = (groupId: string) => {
    // Don't allow removing if there's only one group
    if (formGroups.length <= 1) return;

    setFormGroups((prev) => prev.filter((group) => group.id !== groupId));

    // Also remove any errors for this group
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[groupId];
      return newErrors;
    });
  };

  const validate = () => {
    const newErrors: Record<string, Record<string, string>> = {};
    let isValid = true;

    formGroups.forEach((group) => {
      const groupErrors: Record<string, string> = {};

      if (!group.level) {
        groupErrors.level = 'Level is required.';
      } else if (!/^\d+$/.test(group.level)) {
        groupErrors.level = 'Level must be a valid number.';
      } else if (parseInt(group.level, 10) < 1) {
        groupErrors.level = 'Level must be greater than 0.';
      }

      if (!group.department || group.department.length === 0) groupErrors.department = 'At least one department is required.';
      if (!group.designations.length) groupErrors.designations = 'At least one designation is required.';

      if (Object.keys(groupErrors).length > 0) {
        newErrors[group.id] = groupErrors;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validate()) return;

    // Clear any previous backend error
    setBackendError('');

    try {
      // Only support single group edit for now (edit from grid)
      if (formGroups.length === 1 && formGroups[0].id && !Number.isNaN(Number(formGroups[0].id))) {
        // Edit mode: call PUT
        const group = formGroups[0];
        const buItems = buData.filter((bu) => group.department.includes(bu.department));
        const designationsWithIds = Array.from(
          new Set(
            group.designations.map((designationName) => {
              const designationObj = availableDesignations.find((d) => d.name === designationName);
              return designationObj ? designationObj.id : designationName;
            }),
          ),
        );
        // Convert level string to integer
        const levelValue = group.level ? parseInt(group.level, 10) : null;
        const payload: any = {
          name: group.groupName ? group.groupName.trim() : '',
          level: levelValue,
          designations: designationsWithIds,
        };
        if (buItems.length === 1) {
          payload.department_id = buItems[0]?.id;
        } else if (buItems.length > 1) {
          // Prefer sending array if backend supports; fallback handled server-side
          payload.department_ids = buItems.map((b) => b.id);
        }

        try {
          // Use direct axios call to get raw error response
          const response = await axios.put(`${config.apiBaseUrl}/evaluation/designation-group/${group.id}`, payload, {
            headers: {
              ...getAuthHeaderDynamic(),
              'Content-Type': 'application/json',
            },
          });

          if (response.status === 200 || response.status === 201) {
            showSuccessSnackbar('Designation group updated successfully!');
            navigate('/evaluation', { state: { tabIndex: 0 } });
          }
        } catch (apiError: any) {
          console.error('API Error:', apiError);

          // Extract error message from API response
          let errorMessage = 'Failed to update designation group';

          if (apiError.response?.data) {
            // API returned an error response with data
            const errorData = apiError.response.data;
            if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }
          } else if (apiError.message) {
            // Network or other error
            errorMessage = apiError.message;
          }

          setBackendError(errorMessage);
          showErrorSnackbar(errorMessage);
        }
      } else {
        // Add mode: call POST for all groups
        // For create: expand multi-selected departments into separate payload objects (backward compatible)
        const formattedFormGroups: any[] = [];
        formGroups.forEach((group) => {
          const designationsWithIds = Array.from(
            new Set(
              group.designations.map((designationName) => {
                const designationObj = availableDesignations.find((d) => d.name === designationName);
                return designationObj ? designationObj.id : designationName;
              }),
            ),
          );
          const levelValue = group.level ? parseInt(group.level, 10) : null;
          const selectedBuItems = buData.filter((bu) => group.department.includes(bu.department));
          if (selectedBuItems.length === 0) {
            formattedFormGroups.push({
              name: group.groupName ? group.groupName.trim() : '',
              level: levelValue,
              designations: designationsWithIds,
            });
          } else {
            selectedBuItems.forEach((bu) => {
              formattedFormGroups.push({
                name: group.groupName ? group.groupName.trim() : '',
                level: levelValue,
                department_id: bu.id,
                designations: designationsWithIds,
              });
            });
          }
        });

        try {
          // Use direct axios call to get raw error response
          const response = await axios.post(`${config.apiBaseUrl}/evaluation/designation-group`, formattedFormGroups, {
            headers: {
              ...getAuthHeaderDynamic(),
              'Content-Type': 'application/json',
            },
          });

          if (response.status === 200 || response.status === 201) {
            showSuccessSnackbar('Designation groups saved successfully!');
            navigate('/evaluation', { state: { tabIndex: 0 } });
          }
        } catch (apiError: any) {
          console.error('API Error:', apiError);

          // Extract error message from API response
          let errorMessage = 'Failed to save designation groups';

          if (apiError.response?.data) {
            // API returned an error response with data
            const errorData = apiError.response.data;
            if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }
          } else if (apiError.message) {
            // Network or other error
            errorMessage = apiError.message;
          }

          setBackendError(errorMessage);
          showErrorSnackbar(errorMessage);
        }
      }
    } catch (error: any) {
      // This should not happen with the nested try-catch above, but keeping as fallback
      console.error('Unexpected error saving designation groups:', error);
      const errorMessage = error.message || 'An unexpected error occurred';
      setBackendError(errorMessage);
      showErrorSnackbar(errorMessage);
    }
  };

  useEffect(() => {
    if (initialData) {
      setFormGroups(initialData);
    }
  }, [initialData]);

  return (
    <Box sx={{ width: '100%', height: '100vh', bgcolor: 'white' }}>
      <Box
        sx={{
          p: 3,
          bgcolor: 'white',
          minHeight: '100vh',
          width: '98%',
          height: '100vh',
          margin: 'auto',
          borderRadius: '12px',
        }}
        className="css-11bgqu7"
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 4,
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={handleBack}
              sx={{
                mr: 1,
                color: '#1976d2',
                p: 0,
              }}
              aria-label="back"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant="h6"
              component="h1"
              sx={{
                fontWeight: 600,
                fontSize: '18px',
                color: '#333',
              }}
            >
              {location.state?.data || initialData ? 'Edit Designation Group' : 'Create Designation Group'}
            </Typography>
          </Box>
          {/* Only show Add Designation Group button in create mode */}
          {!location.state?.data && !initialData && (
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              color="primary"
              onClick={addDesignationGroup}
              sx={{
                mt: 0,
                bgcolor: '#1976d2',
                '&:hover': { bgcolor: '#1565c0' },
                borderRadius: '4px',
                px: 1.5,
                py: 0.5,
                fontSize: '1.3rem',
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: 'none',
                color: '#ffffff',
              }}
            >
              Add Designation Group
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          {formGroups.map((formData) => (
            <Box
              key={formData.id}
              sx={{
                mb: 4,
                p: 3,
                pt: formGroups.length > 1 ? 2 : 3,
                borderRadius: '8px',
                position: 'relative',
                bgcolor: 'white',
                boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.1)',
              }}
            >
              {/* Group Header - Only show if there's a delete button */}
              {formGroups.length > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <IconButton onClick={() => removeDesignationGroup(formData.id)} sx={{ color: 'error.main' }}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}

              {/* Level Field */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography
                    variant="body1"
                    component="label"
                    htmlFor={`level-input-${formData.id}`}
                    sx={{
                      fontWeight: 500,
                      fontSize: '14px',
                      color: '#333',
                    }}
                  >
                    Level<span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Tooltip title={info.level} arrow placement="right">
                    <IconButton sx={{ p: 0, ml: 1 }}>
                      <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  fullWidth
                  id={`level-input-${formData.id}`}
                  placeholder="Enter level number"
                  value={formData.level || ''}
                  onChange={(e) => {
                    const { value } = e.target;
                    // Only allow numbers
                    if (value === '' || /^\d+$/.test(value)) {
                      handleChange(formData.id, 'level', value);
                    }
                  }}
                  error={!!errors[formData.id]?.level}
                  helperText={errors[formData.id]?.level}
                  type="text"
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  sx={{
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-root': {
                      height: '36px',
                      '& fieldset': {
                        borderColor: '#D0D5DD',
                      },
                      '& input': {
                        padding: '8px 12px',
                        fontSize: '14px',
                      },
                    },
                    '& .MuiFormHelperText-root': { margin: '3px 0 0', fontSize: '12px' },
                  }}
                />
              </Box>

              {/* Group Name Field */}
              <Box sx={{ mb: 4, mt: formGroups.length > 1 ? 0 : 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography
                    variant="body1"
                    component="label"
                    htmlFor={`group-name-${formData.id}`}
                    sx={{
                      fontWeight: 500,
                      fontSize: '14px',
                      color: '#333',
                    }}
                  >
                    Group Name
                  </Typography>
                  <Tooltip title={info.groupName} arrow placement="right">
                    <IconButton sx={{ p: 0, ml: 1 }}>
                      <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  fullWidth
                  id={`group-name-${formData.id}`}
                  placeholder="Enter group name"
                  value={formData.groupName}
                  onChange={(e) => handleChange(formData.id, 'groupName', e.target.value)}
                  error={!!errors[formData.id]?.groupName}
                  helperText={errors[formData.id]?.groupName}
                  sx={{
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-root': {
                      height: '36px',
                      '& input': {
                        padding: '8px 12px',
                        fontSize: '14px',
                      },
                      '& fieldset': {
                        borderColor: '#D0D5DD',
                      },
                    },
                    '& .MuiFormHelperText-root': { margin: '3px 0 0', fontSize: '12px' },
                  }}
                />
              </Box>

              {/* Departments Field (multi-select) */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography
                    variant="body1"
                    component="label"
                    htmlFor={`department-select-${formData.id}`}
                    sx={{
                      fontWeight: 500,
                      fontSize: '14px',
                      color: '#333',
                    }}
                  >
                    Departments<span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Tooltip title={info.department} arrow placement="right">
                    <IconButton sx={{ p: 0, ml: 1 }}>
                      <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Autocomplete
                  multiple
                  id={`department-select-${formData.id}`}
                  options={buData.map((bu) => bu.department)}
                  value={formData.department}
                  onChange={(e, newValue) => handleChange(formData.id, 'department', newValue || [])}
                  filterSelectedOptions
                  disableCloseOnSelect
                  renderOption={(props, option) => {
                    const isSelected = formData.department.includes(option);
                    return (
                      <li {...props}>
                        <Checkbox checked={isSelected} sx={{ color: '#1976d2' }} />
                        <ListItemText primary={option} />
                      </li>
                    );
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, tagIndex) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index: tagIndex })}
                        size="small"
                        sx={{
                          bgcolor: '#1976d2',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '12px',
                          height: '24px',
                          '& .MuiChip-deleteIcon': {
                            color: 'white',
                            '&:hover': { color: '#e0e0e0' },
                          },
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search and select departments"
                      error={!!errors[formData.id]?.department}
                      helperText={errors[formData.id]?.department}
                      sx={{
                        bgcolor: 'white',
                        '& .MuiOutlinedInput-root': {
                          minHeight: '40px',
                        },
                        '& .MuiFormHelperText-root': { margin: '3px 0 0', fontSize: '12px' },
                      }}
                    />
                  )}
                  fullWidth
                  autoHighlight
                  sx={{
                    '& .MuiOutlinedInput-root': { padding: '4px 8px 4px 8px !important' },
                  }}
                />
              </Box>

              {/* Designations Field */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography
                    variant="body1"
                    component="label"
                    htmlFor={`designations-select-${formData.id}`}
                    sx={{
                      fontWeight: 500,
                      fontSize: '14px',
                      color: '#333',
                    }}
                  >
                    Designations<span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Tooltip title={info.designations} arrow placement="right">
                    <IconButton sx={{ p: 0, ml: 1 }}>
                      <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Autocomplete
                  multiple
                  id={`designations-select-${formData.id}`}
                  options={designationOptions}
                  disableCloseOnSelect
                  loading={loading.allocatedDesignations}
                  isOptionEqualToValue={(option, value) => {
                    const optId = String((option as any).id);
                    const valId = String((value as any).id);
                    const optName = String((option as any).name);
                    const valName = String((value as any).name);
                    return (optId && valId && optId === valId) || optName === valName;
                  }}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    if (option && typeof option === 'object') {
                      const { name } = option as any;
                      if (name === 0) return '0';
                      if (name) return String(name);
                      return '';
                    }
                    return '';
                  }}
                  value={formData.designations.map((des) => {
                    // Try to resolve to existing option by id OR name
                    const found = designationOptions.find((o) => String(o.id) === String(des) || o.name === des);
                    return found || ({ id: String(des), name: String(des) } as any);
                  })}
                  onChange={(_, newValue) => {
                    const processedValue = Array.from(
                      new Set(
                        (newValue ?? [])
                          .map((val) => {
                            if (typeof val === 'string') return val.trim();
                            if (val && typeof val === 'object') return String((val as any).name).trim();
                            return '';
                          })
                          .filter(Boolean),
                      ),
                    );
                    handleChange(formData.id, 'designations', processedValue);
                  }}
                  renderOption={(props, option) => {
                    if (!option) return null;
                    const optionName = typeof option === 'string' ? option : String((option as any).name);
                    const isSelected = formData.designations.includes(optionName);
                    return (
                      <li {...props} key={String((option as any).id) || optionName}>
                        <Checkbox checked={isSelected} sx={{ color: '#1976d2' }} />
                        <ListItemText primary={optionName} />
                      </li>
                    );
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, tagIndex) => {
                      let label = '';
                      if (typeof option === 'string') {
                        label = option;
                      } else if (option && typeof option === 'object') {
                        label = String((option as any).name);
                      }
                      return (
                        <Chip
                          label={label}
                          {...getTagProps({ index: tagIndex })}
                          size="small"
                          sx={{
                            bgcolor: '#1976d2',
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '12px',
                            height: '24px',
                            '& .MuiChip-deleteIcon': {
                              color: 'white',
                              '&:hover': {
                                color: '#e0e0e0',
                              },
                            },
                          }}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search and select designations"
                      error={!!errors[formData.id]?.designations}
                      helperText={errors[formData.id]?.designations}
                      sx={{
                        bgcolor: 'white',
                        '& .MuiOutlinedInput-root': {
                          minHeight: '36px',
                          '& fieldset': {
                            borderColor: '#D0D5DD',
                          },
                        },
                        '& .MuiInputBase-input': {
                          padding: '4px 12px',
                          fontSize: '14px',
                        },
                        '& .MuiFormHelperText-root': { margin: '3px 0 0', fontSize: '12px' },
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          ))}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              sx={{
                color: '#1976d2',
                borderColor: '#1976d2',
                px: 3,
                py: 0.5,
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                height: '36px',
                minWidth: '80px',
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                bgcolor: '#1976d2',
                '&:hover': { bgcolor: '#1565c0' },
                px: 3,
                py: 0.5,
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                height: '36px',
                minWidth: '80px',
                boxShadow: 'none',
                color: '#ffffff',
              }}
            >
              {`${location.state?.data || initialData ? 'Update' : 'Save'}`}
            </Button>
          </Box>

          {/* Confirmation Dialog */}
          <Dialog
            open={openConfirmDialog}
            onClose={handleCloseConfirmDialog}
            aria-labelledby="discard-confirmation-dialog-title"
            aria-describedby="discard-confirmation-dialog-description"
            PaperProps={{
              sx: {
                borderRadius: '8px',
                minWidth: '400px',
                padding: '8px',
              },
            }}
          >
            <DialogTitle id="discard-confirmation-dialog-title" sx={{ pb: 1, fontWeight: 600, fontSize: '18px' }}>
              Confirm Discard
            </DialogTitle>
            <DialogContent sx={{ pb: 2, pt: 0 }}>
              <DialogContentText id="discard-confirmation-dialog-description" sx={{ color: '#333' }}>
                Are you sure you want to discard the form?
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={handleCloseConfirmDialog}
                variant="outlined"
                sx={{
                  color: '#1976d2',
                  borderColor: '#1976d2',
                  textTransform: 'none',
                  fontSize: '14px',
                  minWidth: '80px',
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDiscard}
                variant="contained"
                sx={{
                  bgcolor: '#e53935',
                  '&:hover': { bgcolor: '#c62828' },
                  textTransform: 'none',
                  fontSize: '14px',
                  minWidth: '80px',
                  boxShadow: 'none',
                  color: '#ffffff',
                }}
              >
                Discard
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Box>
  );
};

AddEditDesignationForm.defaultProps = {
  onClose: undefined,
  initialData: null,
};

export default AddEditDesignationForm;
